import { userSettings } from '@lib/settings/userSettings';
import * as Sentry from '@sentry/react';
import { reaction } from 'mobx';
import { debugStore } from './Debug';
import { libraryFavorites } from './LibraryFavorites';
import { paywallStore } from './Paywall';
import { postAuthRedirectStore } from './PostAuthRedirect';
import { socialCapabilitiesStore } from './SocialCapabilities';
import { socialScheduleStore } from './SocialSchedule';
import { trackingStore } from './Tracking';
import { userStore } from './User';

/**
 * Bind every store reaction that reads ANOTHER store's module exports.
 *
 * These must not be registered while the module graph is still evaluating:
 * several stores sit in import cycles (User ↔ router ↔ pages ↔
 * Debug/Tracking/userSettings), so a reaction registered during a store's
 * construction can fire while `userStore`'s binding is still in its temporal
 * dead zone and crash with "Cannot access 'X' before initialization". Even
 * queueMicrotask deferral wasn't reliably late enough — whether it raced
 * module initialization depended on the bundle's chunk evaluation order,
 * which is why dev builds worked while the production bundle broke.
 *
 * Called once from src/index.tsx, whose module body runs only after every
 * static import in the graph has fully evaluated — the one place that is
 * order-proof. Register new cross-store reactions here, never in store
 * constructors.
 */
export function initCrossStoreReactions() {
  trackingStore.initCrossStoreReactions();
  debugStore.initCrossStoreReactions();
  userSettings.initCrossStoreReactions();
  libraryFavorites.initCrossStoreReactions();
  postAuthRedirectStore.initCrossStoreReactions();
  paywallStore.initCrossStoreReactions();
  socialScheduleStore.initCrossStoreReactions();
  socialCapabilitiesStore.initCrossStoreReactions();

  // Id only, never the email: enough to find a customer's events from their user id.
  reaction(
    () => userStore.data?._id,
    (id) => Sentry.setUser(id ? { id } : null),
    { fireImmediately: true },
  );
}
